import { useShareViewDialog } from "@/features/saved-views/share-view-dialog";
import { useToast } from "@faro-lotv/flat-ui";
import { GUID } from "@faro-lotv/foundation";
import { useCallback, type JSX } from "react";

/** The share flow for an annotation or analysis, as the caller has to wire it up. */
type ShareAnnotationOrAnalysis = {
  /** Opens the share dialog for the element. */
  shareAnnotation(): void;

  /** The dialog to render next to the sharing UI. */
  shareDialog: JSX.Element;
};

/**
 * @param id of the annotation or analysis to share
 * @param includeContext whether the current view context should be included
 * @param subject what the element is, named in the share dialog
 * @returns the trigger to share the annotation or analysis, and the dialog to render
 */
export function useShareAnnotationOrAnalysis(
  id: GUID | undefined,
  includeContext: boolean,
  subject: "annotation" | "analysis",
): ShareAnnotationOrAnalysis {
  const { openToast } = useToast();
  const { openShareDialog, shareDialog } = useShareViewDialog();

  const shareAnnotation = useCallback(() => {
    if (!id) {
      openToast({ title: "No element to create link for", variant: "error" });
      return;
    }

    openShareDialog({
      lookAtId: id,
      includeContext,
      description: `Share a link to this ${subject}.`,
    });
  }, [id, includeContext, openShareDialog, openToast, subject]);

  return { shareAnnotation, shareDialog };
}
